import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { scrollToId } from "./Header";
import { ArrowUpRight } from "lucide-react";

const FAQS = [
  {
    q: "Ma TPE/PME est-elle vraiment concernée par le RGPD ?",
    a: "Oui. Dès que vous collectez ou traitez des données personnelles — clients, prospects, salariés, candidats — le RGPD s'applique, quelle que soit la taille de votre structure. La mise en conformité passe par un registre des traitements, des mentions d'information, des mesures de sécurité et l'organisation des droits des personnes. ALMALEX adapte ce dispositif à votre réalité, sans usine à gaz.",
  },
  {
    q: "Qu'est-ce que la LCB-FT et mon entreprise y est-elle soumise ?",
    a: "La lutte contre le blanchiment de capitaux et le financement du terrorisme (LCB-FT) impose à certains secteurs — banques, assurances, immobilier, experts-comptables, jeux d'argent, entre autres — des obligations de vigilance : identification des clients (KYC), analyse des risques, procédures internes et contrôles. Un premier échange permet de déterminer si vous êtes assujetti et de calibrer le dispositif nécessaire.",
  },
  {
    q: "Combien coûte une mise en conformité ?",
    a: "Cela dépend de la taille de votre structure, de votre secteur et de l'ampleur du besoin. Les prestations sont proposées au forfait ou sur devis, annoncés à l'avance en toute transparence — sans surprise. L'objectif d'ALMALEX est précisément de rendre l'accompagnement juridique accessible aux petites structures.",
  },
  {
    q: "Quelle est la différence avec un cabinet d'avocats ?",
    a: "ALMALEX est un service d'accompagnement juridique et de conformité : accompagnement, prévention des risques, rédaction et revue de contrats types, mise en conformité et formation. Il ne s'agit pas de représentation contentieuse devant les tribunaux. Si votre situation l'exige, vous serez orientée vers l'interlocuteur adapté.",
  },
  {
    q: "Combien de temps prend une mission ?",
    a: "De quelques jours pour la revue d'un contrat ou un atelier de sensibilisation, à quelques semaines pour une cartographie des risques ou une mise en conformité RGPD complète. Le calendrier est défini avec vous au démarrage, avec des livrables intermédiaires.",
  },
  {
    q: "Proposez-vous un accompagnement ponctuel ou récurrent ?",
    a: "Les deux. Une mission ponctuelle pour un besoin ciblé (contrat, procédure, formation), ou un accompagnement dans la durée — par exemple un appui DPO externalisé ou un suivi réglementaire — pour sécuriser vos activités en continu.",
  },
];

const FaqItem = ({ item, index, open, onToggle }) => (
  <div className="border-b border-ink-line">
    <button
      type="button"
      data-testid={`faq-trigger-${index + 1}`}
      onClick={onToggle}
      aria-expanded={open}
      className="group flex w-full items-center gap-6 py-7 text-left sm:gap-10"
    >
      <span className="font-mono text-xs tracking-[0.3em] text-mute/50">
        {String(index + 1).padStart(2, "0")}
      </span>
      <span
        className={`flex-1 font-serif text-xl font-medium leading-snug transition-colors duration-300 sm:text-2xl ${
          open ? "text-gold" : "text-ivory group-hover:text-gold"
        }`}
      >
        {item.q}
      </span>
      <span
        className={`flex h-10 w-10 shrink-0 items-center justify-center border transition-all duration-300 ${
          open
            ? "rotate-45 border-gold/70 text-gold"
            : "border-ink-line text-mute group-hover:border-gold/50 group-hover:text-gold"
        }`}
      >
        <Plus className="h-4 w-4" strokeWidth={1.5} />
      </span>
    </button>
    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="overflow-hidden"
        >
          <p
            data-testid={`faq-panel-${index + 1}`}
            className="max-w-3xl pb-8 pl-12 pr-4 text-sm leading-relaxed text-mute sm:pl-[4.5rem] sm:text-base"
          >
            {item.a}
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

export const Faq = () => {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="relative overflow-hidden border-t border-ink-line bg-ink-card/30 py-24 lg:py-36"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-40 top-0 h-[45vh] w-[45vh] rounded-full bg-gold/5 blur-[130px]"
      />
      <div className="relative mx-auto grid max-w-7xl gap-14 px-6 lg:grid-cols-12 lg:px-12">
        <div className="lg:col-span-4">
          <div className="lg:sticky lg:top-28">
            <Reveal>
              <Eyebrow>Questions fréquentes</Eyebrow>
              <h2 className="mt-5 font-serif text-3xl font-medium leading-tight tracking-tight text-ivory sm:text-4xl lg:text-5xl">
                Ce que se demandent les{" "}
                <em className="italic text-gold">TPE & PME</em>
              </h2>
              <p className="mt-6 text-base leading-relaxed text-mute">
                Les réponses aux interrogations les plus courantes sur la
                conformité et l'accompagnement juridique des petites structures.
              </p>
              <a
                href="#contact"
                data-testid="faq-contact-link"
                onClick={(e) => scrollToId(e, "#contact")}
                className="group mt-8 inline-flex items-center gap-3 font-mono text-xs uppercase tracking-[0.22em] text-gold"
              >
                Une autre question ?
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </a>
            </Reveal>
          </div>
        </div>

        <Reveal delay={0.1} className="lg:col-span-8">
          <div className="border-t border-ink-line">
            {FAQS.map((item, i) => (
              <FaqItem
                key={item.q}
                item={item}
                index={i}
                open={openIndex === i}
                onToggle={() => setOpenIndex(openIndex === i ? -1 : i)}
              />
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
